/**
 * BRAND COLLECTION MODAL
 * 
 * Modal for brand owners to create and edit official collections.
 * Uses the same release type structure as polish items for consistency.
 * 
 * Features:
 * - Collection name and description
 * - Release type with conditional fields
 * - Release date, year, season
 * - Collab information
 * - Cover image upload
 * - Event association
 */

import { useState, useRef, useEffect } from 'react';
import { X, Calendar, Sparkles, Image as ImageIcon, Trash2, AlertTriangle } from 'lucide-react';
import { collection, addDoc, updateDoc, doc, serverTimestamp, getDoc, increment } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { normalizeTypography } from '../../utils/normalizeTypography';
import type { BrandCollection } from '../../types/brand';
import { compressImage, ACCEPTED_IMAGE_TYPES } from '../../utils/imageUtils';

// Brand storage limit (500 MB in bytes)
const BRAND_STORAGE_LIMIT = 500 * 1024 * 1024; // 500 MB
const BRAND_STORAGE_WARNING = 400 * 1024 * 1024; // 400 MB - warn at 80%

// Release type options - same as polish items
type ReleaseType = 'core' | 'collection' | 'event' | 'box' | 'groupCustom' | 'exclusive' | 'charity' | 'limitedEdition' | 'convention' | 'other' | 'unknown';

const RELEASE_TYPE_OPTIONS: { value: ReleaseType; label: string; emoji: string; description: string }[] = [
  { value: 'collection', label: 'Standard Collection', emoji: '📚', description: 'A themed collection release' },
  { value: 'core', label: 'Core / Permanent', emoji: '🏠', description: 'Always available products' },
  { value: 'event', label: 'Event Release', emoji: '🎪', description: 'PPU, HHC, or other event' },
  { value: 'box', label: 'Subscription Box', emoji: '📦', description: 'Monthly box or subscription' },
  { value: 'groupCustom', label: 'Group Custom', emoji: '👥', description: 'Custom for a Facebook group, etc.' },
  { value: 'exclusive', label: 'Retailer Exclusive', emoji: '⭐', description: 'Exclusive to a specific retailer' },
  { value: 'charity', label: 'Charity Release', emoji: '💝', description: 'Benefits a charitable cause' },
  { value: 'limitedEdition', label: 'Limited Edition', emoji: '✨', description: 'One-time limited release' },
  { value: 'convention', label: 'Convention Exclusive', emoji: '🎫', description: 'Convention or expo exclusive' },
  { value: 'other', label: 'Other', emoji: '📝', description: 'Other release type' },
];

const SEASON_OPTIONS = [
  { value: '', label: 'No season' },
  { value: 'Spring', label: '🌸 Spring' },
  { value: 'Summer', label: '☀️ Summer' },
  { value: 'Fall', label: '🍂 Fall' },
  { value: 'Winter', label: '❄️ Winter' },
  { value: 'Holiday', label: '🎄 Holiday' },
  { value: 'Valentine', label: '💕 Valentine\'s' },
  { value: 'Halloween', label: '🎃 Halloween' },
];

// Generate year options (current year + 1 down to 2010)
const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [
  { value: '', label: 'No year' },
  ...Array.from({ length: currentYear - 2009 + 1 }, (_, i) => ({
    value: String(currentYear + 1 - i),
    label: String(currentYear + 1 - i)
  }))
];

interface BrandCollectionModalProps {
  brandId: string;
  brandName: string;
  collection?: BrandCollection | null; // For editing existing collection
  onClose: () => void;
  onSave: (collection: BrandCollection) => void;
}

export default function BrandCollectionModal({
  brandId,
  brandName,
  collection: existingCollection,
  onClose,
  onSave
}: BrandCollectionModalProps) {
  const isEditing = !!existingCollection;
  
  // Helper to safely convert Firestore Timestamp or string to date string
  const toDateString = (value: any): string => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (value.toDate && typeof value.toDate === 'function') {
      // Firestore Timestamp - convert to YYYY-MM-DD format
      const date = value.toDate();
      return date.toISOString().split('T')[0];
    }
    if (value instanceof Date) {
      return value.toISOString().split('T')[0];
    }
    return '';
  };
  
  // Basic fields
  const [name, setName] = useState(existingCollection?.name || '');
  const [description, setDescription] = useState(existingCollection?.description || '');
  const [coverImage, setCoverImage] = useState<string | null>(existingCollection?.coverImage || null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Release type
  const [releaseType, setReleaseType] = useState<ReleaseType>((existingCollection?.releaseType as ReleaseType) || 'collection');
  
  // Conditional fields based on releaseType
  const [eventName, setEventName] = useState(existingCollection?.eventName || '');
  const [eventTheme, setEventTheme] = useState(existingCollection?.eventTheme || '');
  const [boxName, setBoxName] = useState(existingCollection?.boxName || '');
  const [boxTheme, setBoxTheme] = useState(existingCollection?.boxTheme || '');
  const [groupName, setGroupName] = useState(existingCollection?.groupName || '');
  const [groupTheme, setGroupTheme] = useState(existingCollection?.groupTheme || '');
  const [exclusiveTo, setExclusiveTo] = useState(existingCollection?.exclusiveTo || '');
  const [exclusiveTheme, setExclusiveTheme] = useState(existingCollection?.exclusiveTheme || '');
  const [charityName, setCharityName] = useState(existingCollection?.charityName || '');
  const [charityCause, setCharityCause] = useState(existingCollection?.charityCause || '');
  const [conventionName, setConventionName] = useState(existingCollection?.conventionName || '');
  
  // Always available fields
  const [collabWith, setCollabWith] = useState(existingCollection?.collabWith || '');
  const [collabTheme, setCollabTheme] = useState(existingCollection?.collabTheme || '');
  const [releaseYear, setReleaseYear] = useState(existingCollection?.releaseYear?.toString() || '');
  const [releaseSeason, setReleaseSeason] = useState(existingCollection?.releaseSeason || '');
  const [releaseDate, setReleaseDate] = useState(toDateString(existingCollection?.releaseDate));
  
  // Submission state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Storage tracking
  const [brandStorageUsed, setBrandStorageUsed] = useState<number>(0);
  const [bytesUploadedThisSession, setBytesUploadedThisSession] = useState<number>(0);
  
  // Fetch current brand storage on mount
  useEffect(() => {
    const fetchBrandStorage = async () => {
      try {
        const brandDoc = await getDoc(doc(db, 'brands', brandId));
        if (brandDoc.exists()) {
          setBrandStorageUsed(brandDoc.data().storageUsed || 0);
        }
      } catch (err) {
        console.error('Error fetching brand storage:', err);
      }
    };
    fetchBrandStorage();
  }, [brandId]);
  
  // Calculate total storage (existing + uploaded this session)
  const totalStorage = brandStorageUsed + bytesUploadedThisSession;
  const isOverLimit = totalStorage > BRAND_STORAGE_LIMIT;
  const isNearLimit = totalStorage > BRAND_STORAGE_WARNING;
  
  // File input ref
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Handle cover image upload with compression and storage tracking
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      // Compress the image before uploading (handles HEIC conversion too)
      const compressedFile = await compressImage(file);

      const storageRef = ref(storage, `brands/${brandId}/collections/${Date.now()}-${compressedFile.name}`);
      await uploadBytes(storageRef, compressedFile, {
        cacheControl: 'public, max-age=31536000, immutable',
        contentType: 'image/jpeg',
      });
      const url = await getDownloadURL(storageRef);
      setCoverImage(url);

      // Update local tracking
      setBytesUploadedThisSession(prev => prev + compressedFile.size);

      // Update brand's storageUsed in Firestore
      await updateDoc(doc(db, 'brands', brandId), {
        storageUsed: increment(compressedFile.size)
      });
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Collection name is required');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      // Helper to remove undefined values (Firebase doesn't allow them)
      const removeUndefined = (obj: Record<string, any>): Record<string, any> => {
        return Object.fromEntries(
          Object.entries(obj).filter(([_, value]) => value !== undefined)
        );
      };
      
      // Build collection data - use null instead of undefined for Firebase.
      // Every user-typed text field gets normalizeTypography so smart quotes
      // and typographic dashes don't create duplicate variants of the same
      // collection/event/box name.
      const collectionData = removeUndefined({
        name: normalizeTypography(name.trim()),
        description: normalizeTypography(description?.trim() ?? '') || null,
        coverImage: coverImage || null,
        releaseType: releaseType || null,

        // Conditional fields based on releaseType
        ...(releaseType === 'event' ? {
          eventName: normalizeTypography(eventName?.trim() ?? '') || null,
          eventTheme: normalizeTypography(eventTheme?.trim() ?? '') || null,
        } : {}),
        ...(releaseType === 'box' ? {
          boxName: normalizeTypography(boxName?.trim() ?? '') || null,
          boxTheme: normalizeTypography(boxTheme?.trim() ?? '') || null,
        } : {}),
        ...(releaseType === 'groupCustom' ? {
          groupName: normalizeTypography(groupName?.trim() ?? '') || null,
          groupTheme: normalizeTypography(groupTheme?.trim() ?? '') || null,
        } : {}),
        ...(releaseType === 'exclusive' ? {
          exclusiveTo: normalizeTypography(exclusiveTo?.trim() ?? '') || null,
          exclusiveTheme: normalizeTypography(exclusiveTheme?.trim() ?? '') || null,
        } : {}),
        ...(releaseType === 'charity' ? {
          charityName: normalizeTypography(charityName?.trim() ?? '') || null,
          charityCause: normalizeTypography(charityCause?.trim() ?? '') || null,
        } : {}),
        ...(releaseType === 'convention' ? {
          conventionName: normalizeTypography(conventionName?.trim() ?? '') || null,
        } : {}),

        // Always available fields
        collabWith: normalizeTypography(collabWith?.trim() ?? '') || null,
        collabTheme: normalizeTypography(collabTheme?.trim() ?? '') || null,
        releaseYear: releaseYear ? parseInt(releaseYear) : null,
        releaseSeason: releaseSeason || null,
        releaseDate: releaseDate || null,
        
        updatedAt: serverTimestamp(),
      });
      
      if (isEditing && existingCollection?.id) {
        // Update existing collection
        await updateDoc(
          doc(db, 'brands', brandId, 'collections', existingCollection.id),
          collectionData
        );
        
        onSave({ ...existingCollection, ...collectionData, id: existingCollection.id } as BrandCollection);
      } else {
        // Create new collection
        const newCollectionData = {
          ...collectionData,
          brandId,
          brandName,
          items: [],
          createdAt: serverTimestamp(),
        };
        
        const docRef = await addDoc(
          collection(db, 'brands', brandId, 'collections'),
          newCollectionData
        );

        // Keep the denormalized count on the brand doc in sync so the
        // manage-page storage/stats read matches what the tab shows.
        // Best-effort — if the increment fails the collection is still
        // created, we just log and move on.
        try {
          await updateDoc(doc(db, 'brands', brandId), {
            collectionsCount: increment(1),
            updatedAt: serverTimestamp(),
          });
        } catch (err) {
          console.warn('collectionsCount increment failed:', err);
        }

        onSave({ ...newCollectionData, id: docRef.id } as unknown as BrandCollection);
      }
      
      onClose();
    } catch (err) {
      console.error('Error saving collection:', err);
      setError('Failed to save collection');
    } finally {
      setSaving(false);
    }
  };

  // Render conditional fields based on release type
  const renderReleaseFields = () => {
    switch (releaseType) {
      case 'event':
        return (
          <div className="mt-3 p-4 bg-pink-50 border border-pink-200 rounded-lg space-y-3">
            <p className="text-sm text-pink-700 font-medium">🎪 Event Details</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-pink-700 mb-1">
                  Event Name *
                </label>
                <input
                  type="text"
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  className="w-full px-3 py-2 border border-pink-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="e.g., Polish Pickup, HHC"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-pink-700 mb-1">
                  Event Theme
                </label>
                <input
                  type="text"
                  value={eventTheme}
                  onChange={(e) => setEventTheme(e.target.value)}
                  className="w-full px-3 py-2 border border-pink-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="e.g., Periodic Table, Autumn Harvest"
                />
              </div>
            </div>
          </div>
        );
      
      case 'box':
        return (
          <div className="mt-3 p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
            <p className="text-sm text-amber-700 font-medium">📦 Subscription Box Details</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-amber-700 mb-1">
                  Box Name *
                </label>
                <input
                  type="text"
                  value={boxName}
                  onChange={(e) => setBoxName(e.target.value)}
                  className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="e.g., Polished Gamers Box, Holo Hookup"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-amber-700 mb-1">
                  Box Theme / Month
                </label>
                <input
                  type="text"
                  value={boxTheme}
                  onChange={(e) => setBoxTheme(e.target.value)}
                  className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="e.g., October 2024, Cozy Season"
                />
              </div>
            </div>
          </div>
        );
      
      case 'groupCustom':
        return (
          <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
            <p className="text-sm text-blue-700 font-medium">👥 Group Custom Details</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-blue-700 mb-1">
                  Group Name *
                </label>
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Reddit Lacqueristas, Polish Addicts FB"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-700 mb-1">
                  Theme (optional)
                </label>
                <input
                  type="text"
                  value={groupTheme}
                  onChange={(e) => setGroupTheme(e.target.value)}
                  className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Anniversary Collection"
                />
              </div>
            </div>
          </div>
        );
      
      case 'exclusive':
        return (
          <div className="mt-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg space-y-3">
            <p className="text-sm text-yellow-700 font-medium">⭐ Retailer Exclusive Details</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-yellow-700 mb-1">
                  Exclusive To *
                </label>
                <input
                  type="text"
                  value={exclusiveTo}
                  onChange={(e) => setExclusiveTo(e.target.value)}
                  className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="e.g., Live Love Polish, Holo Taco"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-yellow-700 mb-1">
                  Theme (optional)
                </label>
                <input
                  type="text"
                  value={exclusiveTheme}
                  onChange={(e) => setExclusiveTheme(e.target.value)}
                  className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="e.g., Summer Exclusive"
                />
              </div>
            </div>
          </div>
        );
      
      case 'charity':
        return (
          <div className="mt-3 p-4 bg-rose-50 border border-rose-200 rounded-lg space-y-3">
            <p className="text-sm text-rose-700 font-medium">💝 Charity Release Details</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-rose-700 mb-1">
                  Charity Name *
                </label>
                <input
                  type="text"
                  value={charityName}
                  onChange={(e) => setCharityName(e.target.value)}
                  className="w-full px-3 py-2 border border-rose-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  placeholder="e.g., St. Jude, ASPCA"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-rose-700 mb-1">
                  Cause / Campaign
                </label>
                <input
                  type="text"
                  value={charityCause}
                  onChange={(e) => setCharityCause(e.target.value)}
                  className="w-full px-3 py-2 border border-rose-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  placeholder="e.g., Breast Cancer Awareness"
                />
              </div>
            </div>
          </div>
        );
      
      case 'convention':
        return (
          <div className="mt-3 p-4 bg-violet-50 border border-violet-200 rounded-lg space-y-3">
            <p className="text-sm text-violet-700 font-medium">🎫 Convention Details</p>
            <div>
              <label className="block text-sm font-medium text-violet-700 mb-1">
                Convention Name *
              </label>
              <input
                type="text"
                value={conventionName}
                onChange={(e) => setConventionName(e.target.value)}
                className="w-full px-3 py-2 border border-violet-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                placeholder="e.g., Indie Expo, Polish Con"
              />
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {isEditing ? 'Edit Collection' : 'New Collection'}
            </h2>
            <p className="text-sm text-gray-500">{brandName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Storage Warning Banner */}
          {(isOverLimit || isNearLimit) && (
            <div className={`p-3 rounded-lg flex items-start gap-2 ${
              isOverLimit 
                ? 'bg-red-50 border border-red-200 text-red-700' 
                : 'bg-amber-50 border border-amber-200 text-amber-700'
            }`}>
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">
                  {isOverLimit ? 'Storage limit exceeded' : 'Approaching storage limit'}
                </p>
                <p>
                  Using {(totalStorage / (1024 * 1024)).toFixed(1)} MB of 500 MB.
                  {isOverLimit 
                    ? ' Please contact support to upgrade your brand storage.' 
                    : ' Consider optimizing images or upgrading soon.'}
                </p>
              </div>
            </div>
          )}

          {/* Cover Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cover Image
            </label>
            {coverImage ? (
              <div className="relative">
                <img
                  src={coverImage}
                  alt="Collection cover"
                  className="w-full h-48 object-cover rounded-lg border border-gray-200"
                />
                <button
                  type="button"
                  onClick={() => setCoverImage(null)}
                  className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div
                onClick={() => imageInputRef.current?.click()}
                className="w-full h-48 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors"
              >
                <ImageIcon className="w-10 h-10 text-gray-400 mb-2" />
                <p className="text-gray-500 text-sm">Click to upload cover image</p>
                <p className="text-gray-400 text-xs">Auto-compressed • Recommended: 1200x600px</p>
              </div>
            )}
            <input
              ref={imageInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES}
              onChange={handleImageUpload}
              className="hidden"
            />
            {uploadingImage && (
              <p className="text-sm text-purple-600 mt-2">Uploading...</p>
            )}
          </div>

          {/* Collection Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Collection Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="e.g., Winter Wonderland, Enchanted Forest"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Tell collectors about this collection..."
            />
          </div>

          {/* Release Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Release Type
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {RELEASE_TYPE_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setReleaseType(option.value)}
                  className={`p-3 text-left rounded-lg border-2 transition-all ${
                    releaseType === option.value
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-lg mr-1">{option.emoji}</span>
                  <span className="text-sm font-medium">{option.label}</span>
                </button>
              ))}
            </div>
            
            {/* Conditional fields based on release type */}
            {renderReleaseFields()}
          </div>

          {/* Release Date / Year / Season */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Release Timing
            </label>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Release Date</label>
                <input
                  type="date"
                  value={releaseDate}
                  onChange={(e) => setReleaseDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Year</label>
                <select
                  value={releaseYear}
                  onChange={(e) => setReleaseYear(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {YEAR_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Season</label>
                <select
                  value={releaseSeason}
                  onChange={(e) => setReleaseSeason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {SEASON_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Collaboration Info */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Sparkles className="w-4 h-4 inline mr-1" />
              Collaboration (optional)
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Collab With</label>
                <input
                  type="text"
                  value={collabWith}
                  onChange={(e) => setCollabWith(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Another Brand, Artist Name"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Collab Theme</label>
                <input
                  type="text"
                  value={collabTheme}
                  onChange={(e) => setCollabTheme(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Disney, Lord of the Rings"
                />
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !name.trim()}
              className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Collection'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
